import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { Box, Button, Typography } from '@mui/material';
import {
  FC, memo, useCallback, useContext, useState,
} from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import { DisplayOperationRowEntity } from 'src/entities/DisplayOperationRow.entity';
import { OperationDiff } from 'src/entities/OperationDirection.entity';
import { useOperationDirectionsRequestQuery } from 'src/hooks/useOperationDirections.request.query';
import { ShiftWithCycleModel } from 'src/models/ShiftModel';
import style from 'src/pages/OperationDirectionPresenter/print.module.css';

import LoadingComponent from './LoadingComponent';

export type PresenterProps = {
  shiftWithCycleModel: ShiftWithCycleModel;
  action: '積' | '降' | null;
  unit: 'kg' | '才';
  operations: DisplayOperationRowEntity[];
  isYC: boolean;
  operationDiffs: OperationDiff;
  shiftWithCycleModels: ShiftWithCycleModel[];
  shiftIndex: number;
  // 省略表示（印刷ごとの一時設定。URL の truncate=1 由来）。
  truncate: boolean;
  // 自運行の確定ページ数を親へ報告（ツールバーの「全Nページ」集計／印刷ボタン活性判定用）。
  // detail の計測前は null（未確定）を報告する。
  onPagesMeasured: (shiftId: number, count: number | null) => void;
}

type Props = {
  shiftIds: number[];
  presenter: FC<PresenterProps>;
  action: '積' | '降' | null;
  variant: 'v2' | 'v3';
  // 省略表示（URL の truncate=1 由来。既定 false）。
  truncate: boolean;
}

const OperationDirectionsComponent: FC<Props> = memo(({
  action, shiftIds, presenter: Presenter, variant, truncate,
}) => {
  const licenseContext = useContext(LicenseContext);
  const { data, isError, isLoading } = useOperationDirectionsRequestQuery(shiftIds, variant);

  // 各運行の確定ページ数（shiftId -> count）。ツールバーの「全Nページ」に集計。
  // detail の計測前は null（未確定）。同値の再報告では state を更新せず再レンダーループを防ぐ。
  const [pageCounts, setPageCounts] = useState<Record<number, number | null>>({});
  const handlePagesMeasured = useCallback((shiftId: number, count: number | null) => {
    setPageCounts((prev) => (prev[shiftId] === count ? prev : { ...prev, [shiftId]: count }));
  }, []);

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (isError) {
    return <>Error</>;
  }

  const shiftEntities = data?.shifts || [];
  const operations: DisplayOperationRowEntity[] = data?.operations || [];
  const shiftWithCycleModels = shiftEntities.map((entity) => new ShiftWithCycleModel(entity));

  const isYC = data.isYC || false;
  const operationDiffs = data?.operationDiffs || {};
  // 集計・判定は現在描画中の運行（renderedShiftIds）に限定する。
  // pageCounts には URL の ids 変更等で過去の shiftId が残り得るため、Object.values を直接合算しない。
  const renderedShiftIds = shiftWithCycleModels.filter(Boolean).map((m) => m.entity.id);
  const totalPages = renderedShiftIds.reduce((sum, id) => sum + (pageCounts[id] ?? 0), 0);
  // 印刷準備完了 = 描画中の全運行がページ数を確定報告済み（null でない）。
  // detail はフォント読込待ち等で計測前は null を報告するため、確定まで印刷を無効化し白紙印刷を防ぐ。
  const printReady = renderedShiftIds.length > 0
    && renderedShiftIds.every((id) => pageCounts[id] != null);

  return (
    <div className={style.shell} role="region" aria-label="運行指示書 プレビュー">
      <div className={style.toolbar}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => window.close()}
        >
          戻る
        </Button>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {`運行指示書${truncate ? '（省略表示）' : ''}`}
        </Typography>
        <Box className={style.toolbarRight}>
          <Typography variant="caption" color="text.secondary">
            {printReady ? `全 ${totalPages} ページ` : 'ページ数を計算中…'}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            disabled={!printReady}
          >
            印刷
          </Button>
        </Box>
      </div>
      <div className={style.body}>
        {
          shiftWithCycleModels.map((shiftWithCycleModel, idx) => {
            if (!shiftWithCycleModel) return <div key={['shiftWithCycleModels', idx].join('-')} />;

            return (
              <Presenter
                key={['OperationDirection', shiftWithCycleModel.entity.id].join('-')}
                shiftWithCycleModel={shiftWithCycleModel}
                action={action}
                unit={licenseContext?.config?.unit}
                operations={operations}
                isYC={isYC}
                operationDiffs={operationDiffs}
                shiftWithCycleModels={shiftWithCycleModels}
                shiftIndex={idx}
                truncate={truncate}
                onPagesMeasured={handlePagesMeasured}
              />
            );
          })
        }
      </div>
    </div>
  );
});

export default OperationDirectionsComponent;
